import { useState } from 'react';
import { Bell, BellOff, Check, CheckCheck, Trash2, Info, AlertTriangle, TrendingUp, Calendar, CreditCard } from 'lucide-react';
import { useNotifications } from '@hooks/useNotifications';
import PageHeader from '@components/layout/PageHeader';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import Loading from '@components/common/Loading';
import EmptyState from '@components/common/EmptyState';
import { clsx } from 'clsx';
import { formatDate } from '@utils/formatters';

const typeConfig = {
  info: { icon: Info, color: 'blue', bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600' },
  warning: { icon: AlertTriangle, color: 'yellow', bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-600' },
  investment: { icon: TrendingUp, color: 'green', bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-600' },
  recurring: { icon: Calendar, color: 'purple', bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-600' },
  installment: { icon: CreditCard, color: 'orange', bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-600' },
};

const getTypeConfig = (type) => typeConfig[type] || typeConfig.info;

const NotificationItem = ({ notification, onMarkRead, onDelete }) => {
  const { icon: Icon, bg, text } = getTypeConfig(notification.type);
  const isPending = !notification.isRead;

  return (
    <div
      className={clsx(
        'flex items-start gap-4 p-4 transition-colors group relative',
        isPending
          ? 'bg-primary-50/50 dark:bg-primary-900/10'
          : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
      )}
    >
      {/* Indicador de não lido */}
      {isPending && (
        <span className="absolute left-0 top-0 bottom-0 w-1 bg-primary-500 rounded-r" />
      )}

      {/* Ícone */}
      <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0', bg)}>
        <Icon size={18} className={text} />
      </div>

      {/* Conteúdo */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={clsx(
            'text-sm',
            isPending ? 'font-semibold text-gray-900 dark:text-white' : 'font-medium text-gray-700 dark:text-gray-300'
          )}>
            {notification.title}
          </p>
          <span className="text-xs text-gray-400 flex-shrink-0">
            {notification.createdAt
              ? new Date(notification.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
              : '—'
            }
          </span>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          {notification.message}
        </p>
      </div>

      {/* Ações (visíveis no hover) */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
        {isPending && (
          <button
            onClick={() => onMarkRead(notification.notificationId)}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Marcar como lida"
          >
            <Check size={15} className="text-primary-600" />
          </button>
        )}
        <button
          onClick={() => onDelete(notification.notificationId)}
          className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          title="Excluir"
        >
          <Trash2 size={15} className="text-red-400 hover:text-red-600" />
        </button>
      </div>
    </div>
  );
};

const NotificationsPage = () => {
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'
  const {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    isMarkingAll,
  } = useNotifications();

  const filtered = filter === 'unread'
    ? notifications.filter(n => !n.isRead)
    : notifications;

  if (isLoading) return <Loading text="Carregando notificações..." />;

  return (
    <div>
      <PageHeader
        title="Notificações"
        subtitle="Alertas e avisos da sua conta"
        action={
          unreadCount > 0 && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<CheckCheck size={16} />}
              onClick={markAllAsRead}
              isLoading={isMarkingAll}
            >
              Marcar todas como lidas
            </Button>
          )
        }
      />

      {/* Filtros */}
      <div className="flex items-center gap-1 mb-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-1 w-fit">
        {[
          { value: 'all', label: `Todas (${notifications.length})` },
          { value: 'unread', label: `Não lidas (${unreadCount})` },
        ].map(({ value, label }) => (
          <button
            key={value}
            onClick={() => setFilter(value)}
            className={clsx(
              'px-4 py-1.5 rounded-md text-sm font-medium transition-colors',
              filter === value
                ? 'bg-primary-600 text-white'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Lista */}
      {filtered.length === 0 ? (
        <EmptyState
          icon={<BellOff size={32} />}
          title={filter === 'unread' ? 'Nenhuma notificação não lida' : 'Nenhuma notificação'}
          description={filter === 'unread' ? 'Você está em dia!' : 'Você receberá alertas sobre transações, vencimentos e mais.'}
        />
      ) : (
        <Card padding="none">
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {filtered.map(notification => (
              <NotificationItem
                key={notification.notificationId}
                notification={notification}
                onMarkRead={markAsRead}
                onDelete={deleteNotification}
              />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default NotificationsPage;
